<template>
  <section class="evidence-panel">

    <div v-if="hasEvidenceItems && !readOnly" class="evidence-panel__action-bar">
      <div class="evidence-panel__action-bar-left">
        <span class="evidence-panel__action-dot" aria-hidden="true" />
        <span class="evidence-panel__action-label">
          <template v-if="isEditMode">
            {{ $t('app.assessment_mode.text.input.editing_information') }}
          </template>
          <template v-else>
            <i18n path="app.assessment_mode.text.input.thinkmd_has_understood" tag="span">
              <template v-slot:brand>
                <strong>ThinkMD</strong>
              </template>
            </i18n>
          </template>
        </span>
      </div>

      <div class="evidence-panel__action-bar-right">
        <button
            v-if="!isEditMode"
            type="button"
            class="evidence-panel__btn evidence-panel__btn--edit"
            @click="startEdit"
        >
          {{ $t('app.assessment_mode.text.input.edit') }}
        </button>
        <button
            v-if="isEditMode"
            type="button"
            class="evidence-panel__btn evidence-panel__btn--cancel"
            @click="cancelEdit"
        >
          {{ $t('app.assessment_mode.text.input.cancel') }}
        </button>
        <button
            v-if="isEditMode"
            type="button"
            class="evidence-panel__btn evidence-panel__btn--submit"
            :disabled="isSubmitting || hasValidationErrors"
            @click="handleRhsSubmit"
        >
          <span v-if="isSubmitting">{{ $t('app.assessment_mode.text.input.saving') }}</span>
          <span v-else>{{ $t('app.assessment_mode.text.input.submit_changes') }}</span>
        </button>
      </div>
    </div>

    <div class="evidence-panel__categories">
      <div v-for="category in categories" :key="category.id" class="evidence-panel__category-group">
        <EvidenceCategoryCard
            :category="category"
            :can-edit-items="!readOnly && isEditMode"
            :read-only="readOnly"
            @revert-item="handleRevertItem"
        />

        <transition-group
            v-if="category.id === 'vitals' && activeConflicts.length > 0"
            name="conflict-fade"
            tag="div"
            class="evidence-panel__conflicts"
        >
          <div
              v-for="conflict in activeConflicts"
              :key="conflict.itemId"
              class="evidence-panel__conflict-banner"
          >
            <span class="evidence-panel__conflict-icon">⚠</span>
            <span class="evidence-panel__conflict-text">
              <strong>{{ formatConflictLabel(conflict.fieldName) }}</strong>:
              {{ $t('app.assessment_mode.text.input.conflict_message', {
                transcriptValue: formatConflictValue(conflict.transcriptValue),
                correctedValue: formatConflictValue(conflict.correctedValue),
              }) }}
            </span>
            <button
                type="button"
                class="evidence-panel__conflict-dismiss"
                @click="dismissConflict(conflict.itemId)"
            >
              ✕
            </button>
          </div>
        </transition-group>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, getCurrentInstance, PropType } from 'vue';
import EvidenceCategoryCard from './EvidenceCategoryCard.vue';
import type { EvidenceCategory } from '../../types/text-mode.types';
import { useDraftStore } from '../../stores/draft.store';
import { OBSERVATION_STATUS_LABEL_TOKENS } from '../../constants/observation-status.constants';
import { formatLabel } from '../../utils/text-utils';

export default defineComponent({
  name: 'EvidencePanel',

  components: {
    EvidenceCategoryCard,
  },

  props: {
    categories: {
      type: Array as PropType<EvidenceCategory[]>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const draftStore = useDraftStore();
    const vm = getCurrentInstance();
    const translate = (token: string): string => {
      const t = vm?.proxy.$t;
      return typeof t === 'function' ? String(t.call(vm.proxy, token)) : token;
    };

    const isEditMode = computed(() => draftStore.isEvidenceEditMode);
    const isSubmitting = computed(() => draftStore.isRhsSubmitting);
    const hasValidationErrors = computed(() => draftStore.hasValidationErrors);

    const hasEvidenceItems = computed(() =>
      props.categories.some((cat) => cat.items.length > 0),
    );

    const activeConflicts = computed(() => draftStore.activeConflicts);

    const formatConflictLabel = (fieldName: string): string => translate(fieldName);

    const formatConflictValue = (val: string | number | boolean | null): string => {
      if (val === null || val === undefined) return '—';
      const s = String(val);
      if (OBSERVATION_STATUS_LABEL_TOKENS[s as keyof typeof OBSERVATION_STATUS_LABEL_TOKENS]) {
        return translate(OBSERVATION_STATUS_LABEL_TOKENS[s as keyof typeof OBSERVATION_STATUS_LABEL_TOKENS]);
      }
      return formatLabel(s) || s;
    };

    const startEdit = (): void => {
      draftStore.setEvidenceEditMode(true);
      emit('rhs-edit-start');
    };

    const cancelEdit = (): void => {
      // Revert all draft changes
      draftStore.resetDraft();
    };

    const handleRhsSubmit = async (): Promise<void> => {
      if (draftStore.isRhsSubmitting || draftStore.hasValidationErrors) return;
      if (!draftStore.prepareWarningsForSubmit()) return;
      emit('rhs-submit');
    };

    const dismissConflict = (itemId: string): void => {
      draftStore.dismissConflict(itemId);
    };

    const handleRevertItem = (payload: { itemId: string }): void => {
      emit('revert-item', payload);
    };

    return {
      isEditMode,
      isSubmitting,
      hasValidationErrors,
      hasEvidenceItems,
      activeConflicts,
      formatConflictLabel,
      formatConflictValue,
      startEdit,
      cancelEdit,
      handleRhsSubmit,
      dismissConflict,
      handleRevertItem,
    };
  },
});
</script>
